// Rayon de CAPTURE du drapeau d'une résidence (brief du 12 octobre 2026, docs/routes/README.md §10) : rayon qui
// désigne la résidence candidate quand l'opérateur s'approche du drapeau. Ce n'est ni la zone de travail ni la
// distance de sortie d'une mission (ADR-018) : ces paramètres restent dans le profil de détection.
//   * personnalisé par résidence (`flag_capture_radius_m`), sinon rayon par défaut : `captureRadiusM` du profil de
//     détection actif, à défaut 7 m ;
//   * bornes et pas identiques au serveur (contrainte de colonne et `verify_service_location_flag_v2`) ;
//   * un petit rayon ne garantit aucune précision GPS : il ne fait que restreindre la capture.

export const captureRadiusBounds = { min: 0.5, max: 100, step: 0.5 } as const

/** Rayon de repli sans profil de détection (constante historique des écrans). */
export const fallbackCaptureRadiusMeters = 7

/** Choix enregistré : hériter du défaut, ou valeur propre à la résidence (même égale au défaut). */
export type RadiusChoice = { mode: 'DEFAULT' } | { mode: 'CUSTOM'; meters: number }

export const defaultRadiusChoice: RadiusChoice = { mode: 'DEFAULT' }

/** Choix actuel d'une résidence d'après sa valeur serveur (null = défaut). */
export function radiusChoiceOf(override: number | null | undefined): RadiusChoice {
  return override === null || override === undefined
    ? defaultRadiusChoice
    : { mode: 'CUSTOM', meters: override }
}

export function sameRadiusChoice(a: RadiusChoice, b: RadiusChoice): boolean {
  if (a.mode === 'DEFAULT' || b.mode === 'DEFAULT') return a.mode === b.mode
  return a.meters === b.meters
}

export function effectiveRadius(choice: RadiusChoice, defaultMeters: number): number {
  return choice.mode === 'CUSTOM' ? choice.meters : defaultMeters
}

/** Ramène une valeur dans les bornes, au pas de 0,5 m. */
export function snapRadius(value: number): number {
  const { min, max, step } = captureRadiusBounds
  const snapped = Math.round(value / step) * step
  return Math.min(max, Math.max(min, snapped))
}

/**
 * Saisie libre (virgule ou point) : valeur au pas de 0,5 m, ou null si la saisie n'est pas un nombre dans les
 * bornes. Une valeur hors pas est arrondie au pas le plus proche (7,3 → 7,5).
 */
export function parseCaptureRadius(text: string): number | null {
  const normalized = text.trim().replace(/\s*m$/i, '').replace(',', '.')
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null
  const value = Number(normalized)
  const { min, max } = captureRadiusBounds
  if (!Number.isFinite(value) || value < min || value > max) return null
  return snapRadius(value)
}

/** Un pas de plus (+1) ou de moins (−1), borné. */
export function stepRadius(current: number, direction: 1 | -1): number {
  return snapRadius(current + direction * captureRadiusBounds.step)
}

/** Pincement : rayon de départ multiplié par le rapport des écartements, borné et au pas. */
export function scaleRadius(start: number, ratio: number): number {
  if (!Number.isFinite(ratio) || ratio <= 0) return snapRadius(start)
  return snapRadius(start * ratio)
}

/** « 7 m », « 7,5 m ». */
export function formatCaptureRadius(meters: number): string {
  return `${meters.toLocaleString('fr-CA', { maximumFractionDigits: 1 })} m`
}

/** Le rayon atteint la zone de travail du profil : la capture déborderait sur la manœuvre (avertissement seul). */
export function reachesWorkRadius(meters: number, workRadius: number | null): boolean {
  return workRadius !== null && meters >= workRadius
}
